import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FiUsers, FiBookOpen, FiUserCheck, FiTrendingUp, FiAlertTriangle,
  FiArrowRight, FiCalendar, FiClipboard, FiAward, FiLayers,
} from 'react-icons/fi';
import { userRequest } from '../utils/requestMethods';
import { displaySchoolTerm } from '../utils/schoolYearUtils';
import { useStateContext } from '../contexts/ContextProvider';
import useKYCStatusRedirect from '../hooks/useKYCStatusRedirect';
import { CompleteAccountSetup, AddTermModal } from '../components';
import { differenceInDays, isAfter, isSameDay, format } from 'date-fns';

// Tailwind can't resolve interpolated class names, so map tones explicitly.
const TONES = {
  indigo: { chip: 'bg-indigo-50 text-indigo-600', value: 'text-gray-900' },
  blue: { chip: 'bg-blue-50 text-blue-600', value: 'text-gray-900' },
  emerald: { chip: 'bg-emerald-50 text-emerald-600', value: 'text-emerald-700' },
  amber: { chip: 'bg-amber-50 text-amber-600', value: 'text-amber-700' },
  rose: { chip: 'bg-rose-50 text-rose-600', value: 'text-rose-700' },
};

const greetingFor = (hour) =>
  hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

const StatCard = ({ icon: Icon, label, value, hint, tone = 'indigo', loading }) => {
  const t = TONES[tone];
  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:bg-secondary-dark-bg p-5">
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`p-2 rounded-lg ${t.chip}`}>
          <Icon className="w-4 h-4" />
        </div>
        <p className="text-xs font-medium text-gray-500">{label}</p>
      </div>
      {loading ? (
        <div className="h-8 w-20 bg-gray-100 rounded animate-pulse" />
      ) : (
        <p className={`text-2xl font-bold ${t.value}`}>{value}</p>
      )}
      {hint && !loading && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
};

const Home = () => {
  useKYCStatusRedirect("/home");
  const { user, school } = useStateContext();
  const schoolId = school?._id;
  const schoolCode = school?.code;
  // Memoised so the `|| []` fallback doesn't hand the memos below a new array
  // reference on every render.
  const schoolTerms = useMemo(() => school?.settings?.terms || [], [school]);
  const schoolTerm = school?.settings?.current_term;
  const schoolSession = school?.settings?.current_session;

  const [classes, setClasses] = useState(null);
  const [teacherCount, setTeacherCount] = useState(null);
  const [attendanceRate, setAttendanceRate] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Functional form: this is captured by the `issues` memo, which doesn't
  // re-run on isModalOpen changes and would otherwise hold a stale value.
  const toggleModal = () => setIsModalOpen(v => !v);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const [classesRes, teachersRes] = await Promise.all([
          userRequest.get(`classes?code=${schoolCode}`),
          userRequest.get(`/teachers?school_id=${schoolId}&limit=100`),
        ]);
        setClasses(classesRes.data.classes || []);
        setTeacherCount((teachersRes.data.teachers || []).length);
      } catch (error) {
        console.log(error);
        setClasses([]);
        setTeacherCount(0);
      }
    };
    if (schoolId && schoolCode) fetchOverview();
  }, [schoolId, schoolCode]);

  // Omitting class_id asks /attendance/stats to aggregate across the whole
  // school, so this is one request rather than one per class — and it no
  // longer waits on the class list, so it loads in parallel.
  useEffect(() => {
    const fetchAttendance = async () => {
      setAttendanceLoading(true);
      try {
        const params = new URLSearchParams({
          school_id: schoolId,
          term: schoolTerm,
          session: schoolSession,
        });
        const { data } = await userRequest.get(`/attendance/stats?${params.toString()}`);
        // A rate of 0 with days recorded is a real 0%; no days recorded means
        // nothing has been marked yet, which is a dash rather than a zero.
        const hasDays = Number(data?.total_days) > 0;
        const rate = Number(data?.total_attendance_rate);
        setAttendanceRate(hasDays && !isNaN(rate) ? Math.round(rate) : null);
      } catch (error) {
        console.log(error);
        setAttendanceRate(null);
      } finally {
        setAttendanceLoading(false);
      }
    };
    if (schoolId && schoolTerm && schoolSession) fetchAttendance();
  }, [schoolId, schoolTerm, schoolSession]);

  // Where we are in the term — the page's anchor.
  const term = useMemo(() => {
    const current = schoolTerms.find(t => t.term === schoolTerm);
    if (!current?.start_date || !current?.end_date) return null;
    const start = new Date(current.start_date);
    const end = new Date(current.end_date);
    const today = new Date();
    const totalDays = differenceInDays(end, start);
    if (!totalDays || totalDays <= 0) return null;
    const elapsed = Math.max(0, Math.min(totalDays, differenceInDays(today, start)));
    return {
      end,
      pct: Math.round((elapsed / totalDays) * 100),
      week: Math.max(1, Math.ceil((elapsed + 1) / 7)),
      totalWeeks: Math.max(1, Math.ceil(totalDays / 7)),
      daysLeft: Math.max(0, differenceInDays(end, today)),
      ended: isAfter(today, end) || isSameDay(today, end),
    };
  }, [schoolTerms, schoolTerm]);

  const totalStudents = (classes || []).reduce((a, c) => a + (c.studentCount || 0), 0);
  const totalSubjects = (classes || []).reduce((a, c) => a + (c.subjectCount || 0), 0);

  // Exceptions worth acting on, derived from data already on the wire.
  const issues = useMemo(() => {
    const list = [];
    if (schoolTerms.length === 0) {
      list.push({
        key: 'no-terms',
        title: 'Term dates not set',
        body: 'Add start and end dates so results and invoices line up with your calendar.',
        to: '/settings',
        cta: 'Add terms',
      });
    }
    if (term?.ended) {
      list.push({
        key: 'term-ended',
        title: `${displaySchoolTerm(schoolTerm)} has ended`,
        body: `It ended on ${format(term.end, 'd MMMM yyyy')}. Set up the next term to continue.`,
        onClick: toggleModal,
        cta: 'Set up next term',
      });
    }
    if (classes) {
      const noTeacher = classes.filter(c => !(c.teachers?.length));
      const noSubjects = classes.filter(c => !c.subjectCount);
      if (noTeacher.length) {
        list.push({
          key: 'no-teacher',
          title: `${noTeacher.length} ${noTeacher.length === 1 ? 'class has' : 'classes have'} no teacher`,
          body: noTeacher.slice(0, 3).map(c => c.class_name).join(', ')
            + (noTeacher.length > 3 ? ` and ${noTeacher.length - 3} more` : ''),
          to: '/classes',
          cta: 'Assign teachers',
        });
      }
      if (noSubjects.length) {
        list.push({
          key: 'no-subjects',
          title: `${noSubjects.length} ${noSubjects.length === 1 ? 'class has' : 'classes have'} no subjects`,
          body: noSubjects.slice(0, 3).map(c => c.class_name).join(', ')
            + (noSubjects.length > 3 ? ` and ${noSubjects.length - 3} more` : ''),
          to: '/classes',
          cta: 'Add subjects',
        });
      }
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, term, schoolTerms, schoolTerm]);

  const quickActions = [
    { to: '/classes', icon: FiBookOpen, tone: 'indigo', label: 'Classes', desc: 'Set up classes, subjects and fees' },
    { to: '/management/teacher', icon: FiUserCheck, tone: 'blue', label: 'Teachers', desc: 'Manage staff and assignments' },
    { to: '/management/attendance', icon: FiClipboard, tone: 'emerald', label: 'Attendance', desc: 'Mark and review daily registers' },
    { to: '/result-management/upload-class-result', icon: FiAward, tone: 'amber', label: 'Results', desc: 'Upload scores and publish results' },
  ];

  const loading = classes === null;

  return (
    <div className="mt-10 pb-10">
      {/* ── Greeting + where we are in the term ─────────────────────── */}
      <div className="mx-4 md:mx-10 mt-8">
        <div className="rounded-3xl border border-gray-200 bg-white dark:bg-secondary-dark-bg p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-extrabold text-gray-900 dark:text-gray-100">
                {greetingFor(new Date().getHours())}, {user?.firstname}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {format(new Date(), 'EEEE, d MMMM yyyy')}
              </p>
            </div>
            {schoolTerms.length > 0 && (
              <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700 bg-indigo-50 rounded-full px-4 py-2 shrink-0">
                <FiCalendar className="w-4 h-4" />
                {displaySchoolTerm(schoolTerm)} · {schoolSession}
              </div>
            )}
          </div>

          {term && !term.ended && (
            <div className="mt-6">
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">
                  Week {term.week} of {term.totalWeeks}
                </p>
                <p className={`text-sm font-semibold ${term.daysLeft <= 7 ? 'text-amber-600' : 'text-gray-500'}`}>
                  {term.daysLeft === 0 ? 'Ends today'
                    : term.daysLeft === 1 ? '1 day left'
                    : `${term.daysLeft} days left`}
                </p>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all duration-700 ease-out ${
                    term.daysLeft <= 7 ? 'bg-amber-500' : 'bg-indigo-600'
                  }`}
                  style={{ width: `${term.pct}%` }}
                />
              </div>
              {term.daysLeft <= 7 && (
                <p className="text-xs text-amber-700 mt-2">
                  Term ends soon — make sure results and reports are up to date before it closes.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Needs attention — only when something actually does ─────── */}
      {issues.length > 0 && (
        <div className="mx-4 md:mx-10 mt-6">
          <div className="flex items-center gap-2 mb-3">
            <FiAlertTriangle className="w-4 h-4 text-amber-500" />
            <p className="text-sm font-semibold text-gray-800">Needs attention</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {issues.map(issue => {
              const inner = (
                <div className="h-full rounded-2xl border border-amber-200 bg-amber-50 p-5 hover:bg-amber-100/70 transition-colors text-left">
                  <p className="text-sm font-semibold text-gray-900">{issue.title}</p>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">{issue.body}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 mt-3">
                    {issue.cta} <FiArrowRight className="w-3 h-3" />
                  </span>
                </div>
              );
              return issue.to ? (
                <Link key={issue.key} to={issue.to}>{inner}</Link>
              ) : (
                <button key={issue.key} type="button" onClick={issue.onClick} className="w-full text-left">
                  {inner}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── At a glance ─────────────────────────────────────────────── */}
      <div className="mx-4 md:mx-10 mt-6">
        <p className="text-sm font-semibold text-gray-800 mb-3">At a glance</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={FiUsers} label="Students" tone="indigo" loading={loading}
            value={totalStudents}
            hint={classes?.length ? `across ${classes.length} ${classes.length === 1 ? 'class' : 'classes'}` : undefined}
          />
          <StatCard
            icon={FiBookOpen} label="Classes" tone="blue" loading={loading}
            value={classes?.length ?? 0}
            hint={totalSubjects ? `${totalSubjects} subjects` : undefined}
          />
          <StatCard
            icon={FiUserCheck} label="Teachers" tone="amber" loading={teacherCount === null}
            value={teacherCount ?? 0}
          />
          <StatCard
            icon={FiTrendingUp} label="Attendance" tone="emerald"
            loading={attendanceLoading}
            value={attendanceRate === null ? '—' : `${attendanceRate}%`}
            hint={attendanceRate === null ? 'No attendance marked yet' : 'this term, all classes'}
          />
        </div>
      </div>

      {/* ── Quick actions ───────────────────────────────────────────── */}
      <div className="mx-4 md:mx-10 mt-6">
        <p className="text-sm font-semibold text-gray-800 mb-3">Quick actions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {quickActions.map(({ to, icon: Icon, tone, label, desc }) => (
            <Link key={label} to={to}>
              <div className="h-full flex items-start gap-3 px-4 py-4 rounded-2xl border border-gray-200 bg-white dark:bg-secondary-dark-bg hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors">
                <div className={`p-2 rounded-lg shrink-0 ${TONES[tone].chip}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{label}</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* ── Empty state for a brand-new school ──────────────────────── */}
      {!loading && classes.length === 0 && (
        <div className="mx-4 md:mx-10 mt-6 rounded-2xl border border-dashed border-gray-300 p-10 text-center">
          <FiLayers className="w-6 h-6 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-700">No classes yet</p>
          <p className="text-xs text-gray-500 mt-1 mb-4">
            Create your first class to start adding students, subjects and results.
          </p>
          <Link to="/classes">
            <button className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-full transition-colors">
              Go to Classes
            </button>
          </Link>
        </div>
      )}

      <div className="mx-4 md:mx-10 mt-6">
        <CompleteAccountSetup school={school} />
      </div>

      {isModalOpen && (
        <AddTermModal closeModal={toggleModal} schoolId={school?._id} school={school} />
      )}
    </div>
  );
};

export default Home;
